import React, { useState } from "react";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Typography, Button, Space, Empty } from "antd";
import arrowIcon from 'assets/arrow.png';
import jsPDF from "jspdf"
import { generateTopLocationsPDF } from '../utils/generateTopLocationsPDF';
import { useSelector } from 'react-redux';

const { Text } = Typography;

ChartJS.register(ArcElement, Tooltip, Legend);

const BarGender = ({ location, locationGenderData, topLocationsRef, setIndividualExportLoading, setExportMessage, isPermanentLocation }) => {
  const corporateData = useSelector((state) => state?.auth?.corporateData);
  const locationChartData = useSelector(state => state?.corporateDashboardPage2?.locationChartData);
  
  // Check if no location is selected or if location is empty/null
  // const isNoSelection = !location || location === 'None' || location === '';

  // // Check if there's any data for locations (overall data, not just selected location)
  // const hasData = locationChartData?.locationBarChart?.some(item => 
  //   item.location && item.location.trim() !== "" && (item.hired || 0) > 0
  // );

  // Find the gender data for the selected location from the new API structure
  // Only look for valid locations (non-empty strings)
  const selectedLocationData = location && location !== '' && locationGenderData
    ? locationGenderData.find(item => {
        const itemLocation = item.location || "Unknown";
        return itemLocation === location || 
               (location === "Unknown" && (itemLocation === "Unknown" || itemLocation === "" || !itemLocation));
      })
    : null;

  // Check if no location is selected or if location is empty/null or if no gender data exists for selected location
  const isNoSelection = !location || location === 'None' || location === '' || !selectedLocationData;

  // Check if there's any data for locations (overall data, not just selected location)
  const hasData = locationChartData?.locationBarChart?.some(item => (item.hired || 0) > 0 || (item.floated || 0) > 0);

  // Check if there's valid data for export (excluding Unknown)
  const hasValidExportData = locationChartData?.locationBarChart?.some(item => 
    item.location && item.location !== "Unknown" && ((item.hired || 0) > 0 || (item.floated || 0) > 0)
  );
  
  const genderAnalysis = selectedLocationData?.locationGenderAnalysis || {
    men: 0,
    women: 0,
    others: 0,
    totalCandidate: 0,
    malePercent: 0,
    femalePercent: 0,
    othersPercent: 0
  };

  const { men, women, others, totalCandidate, malePercent, femalePercent, othersPercent } = genderAnalysis;
  const total = men + women + others;

  // Use API percentages if available, otherwise calculate
  const menPercentage = total > 0 ? (malePercent || Math.round((men / total) * 100)) : 0;
  const womenPercentage = total > 0 ? (femalePercent || Math.round((women / total) * 100)) : 0;
  const othersPercentage = total > 0 ? (othersPercent || Math.round((others / total) * 100)) : 0;

  // === Data for 2025 only ===
  const data = {
    labels: (isNoSelection || total === 0) ? [''] : (others > 0 ? ["Male", "Female", "Others"] : ["Male", "Female"]),
    datasets: [
      {
        data: isNoSelection ? [100] : (total === 0 ? [100] : (others > 0 ? [men, women, others] : [men, women])),
        backgroundColor: isNoSelection ? ['#D9D9D9'] : (total === 0 ? ['#D9D9D9'] : (others > 0 ? ["rgba(13, 58, 133, 1)", "rgba(1, 138, 190, 1)", "rgba(145, 225, 255, 1)"] : ["#001F54", "#007B9E"])),
        borderWidth: 0,
        cutout: "65%", // gives thinner donut ring like your image
      },
    ],
  };
  const handleExport = async () => {
    try {
      setIndividualExportLoading(true);
      setExportMessage('Exporting Top Locations...');
      const pdf = new jsPDF("p", "mm", "a4")

      await generateTopLocationsPDF({
        pdf,
        corporateData,
        locationChartData,
        topLocationsRef,
        locationName: isPermanentLocation ? "Permanent Location" : "Current Location"
      })

      pdf.save("Top_Locations.pdf")
    } finally {
      setIndividualExportLoading(false);
    }
  }

  // === Chart Options ===
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          // Show number with commas in tooltip, but show 0 when no selection or no data
          label: (context) => {
            if (isNoSelection || total === 0) {
              return `: 0`;
            }
            return `${context.label}: ${context.parsed.toLocaleString()}`;
          }, 
        },
      },
    },
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* === Header === */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: 8,
          gap: 8,
        }}
      >
        <Text style={{ fontSize: 20, fontWeight: 600, margin: 0, color: '#000' }}>
          Gender Analysis{location && " – "}
          {location && (
            <span style={{ color: "rgba(52, 62, 89, 1)", fontFamily: 'Plus Jakarta Sans', weight: '400' }}>{location}</span>
          )}
        </Text>
        <Button
            type="default"
            onClick={handleExport}
            disabled={!hasValidExportData}
            style={{
              borderColor: "#00a878",
              color: "#00a878",
              fontWeight: 500,
              borderRadius: 6,
              height: 30,
              marginLeft: "auto",
            }}
          >
            Export
          </Button>
      </div>

      {hasData ? (
        <>
      {/* === Donut + Info === */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: 40,
          marginTop: 40,
          flexWrap: "wrap",
        }}
      >
        {/* Donut Chart */}
        <div style={{ 
          position: "relative", 
          width: 280, 
          height: 280, 
          marginLeft: 80,
          transition: "all 0.3s ease",  }}>

          <Doughnut data={data} options={options} />
        </div>

        {/* Info Labels */}
        {location && (
          <div
            key={`info-${location}`} // Force re-render when location changes
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 24,
              justifyContent: "center",
            }}
          >
            {/* Male */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: "rgba(13, 58, 133, 1)",
                }}
              ></div>
              <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                <Text strong style={{fontSize: 20,fontWeight: '500', fontFamily: 'Plus Jakarta Sans', color:'rgba(0, 0, 0, 1)'}}>Male</Text>
                <Text strong style={{ fontSize: 18 }}>{menPercentage}%</Text>
              </div>
            </div>

            {/* Female */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: "rgba(1, 138, 190, 1)",
                }}
              ></div>
              <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                <Text strong style={{ fontSize: 20,fontWeight: '500', fontFamily: 'Plus Jakarta Sans', color:'rgba(0, 0, 0, 1)'}}>Female</Text>
                <Text strong style={{ fontSize: 18 }}>{womenPercentage}%</Text>
              </div>
            </div>

            {/* Others */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: "rgba(145, 225, 255, 1)",
                }}
              ></div>
              <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                <Text strong style={{ fontSize: 16 }}>Others</Text>
                <Text strong style={{ fontSize: 18 }}>{othersPercentage}%</Text>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* === Bottom Info Box === */}
      <div
        style={{
          marginTop: location ? 96 : 92,
          backgroundColor: "#fff",
          border: location ? "1px solid #435fe8" : "1px solid rgba(194, 194, 194, 1)",
          borderRadius: '8px',
          padding: '12px 16px',
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {location ? (
          <>
            <Text strong type="secondary" style={{ fontSize: 14, display: 'flex-start' }}>
              Showing
            </Text>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: "rgba(0, 27, 72, 1)",
                  borderRadius: "50%",
                }}
              ></div>
              <Text style={{ fontSize: 13 }}>
                <strong>Men:</strong> {men}
              </Text>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: "rgba(1, 138, 190, 1)",
                  borderRadius: "50%",
                }}
              ></div>
              <Text style={{ fontSize: 13 }}>
                <strong>Women:</strong> {women}
              </Text>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    backgroundColor: "rgba(145, 225, 255, 1)",
                    borderRadius: "50%",
                  }}
                ></div>
                <Text style={{ fontSize: 13 }}>
                  <strong>Others:</strong> {others}
                </Text>
              </div>
          </>
        ) : (
          <Space size={8}>
            <svg width="18" height="21" viewBox="0 0 18 21" style={{ marginLeft: '2px' }}>
              <rect 
                x="10.05" y="3" width="4.022172927856445" height="12" fill="rgba(50, 73, 215, 1)"opacity="1"
              />
              <rect 
                x="10.05" y="17" width="4.022172927856445" height="4" fill="rgba(50, 73, 215, 1)" opacity="1"
              />
            </svg>
            <Text style={{ 
              color: '#262626',
              fontFamily: 'Plus Jakarta Sans',fontWeight: 400,fontSize: 14,lineHeight: '18px',letterSpacing: '0%'
            }}>
              Note: Click the graph for more details.
            </Text>
          </Space>
        )}
      </div>
        </>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', flexGrow: 1 }}>
          <Empty />
        </div>
      )}
    </div>
  );
};

export default BarGender;
